/**
 * 市场项目散点（前期 / 履约）
 */

import type * as echarts from 'echarts';
import type { MarketProject, ProjectCategory } from '../../../types/market';
import { SCATTER_ALTITUDE } from '../mapConstants';
import type { ScatterDatum } from '../types';

const COLOR_PRELIMINARY = '#3dd68c';
const COLOR_PERFORMANCE = '#fa8c16';
const COLOR_OVERSEAS = '#b37feb';

function categoryColor(
  category: ProjectCategory,
  isOverseas: boolean,
): string {
  if (isOverseas) return COLOR_OVERSEAS;
  return category === 'preliminary'
    ? COLOR_PRELIMINARY
    : COLOR_PERFORMANCE;
}

interface PointColors {
  color: string;
  opacity: number;
}

const COORD_KEY_PRECISION = 4;
/** 同坐标簇最大偏移（度），省图下约 5～8 km，需明显可辨 */
const JITTER_MAX_RADIUS_DEG = 0.07;
/** 半径不低于最大值的该比例，避免点都挤在中心 */
const JITTER_MIN_RADIUS_RATIO = 0.5;

function hashString(input: string): number {
  let h = 0;
  for (let i = 0; i < input.length; i += 1) {
    h = (Math.imul(31, h) + input.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}

/** 确定性伪随机偏移，避免等分圆环的「钟表」观感 */
export function offsetForClusterMember(
  projectId: string,
  index: number,
  groupSize: number,
): { dLng: number; dLat: number } {
  const h = hashString(`${projectId}:${index}`);
  const angle =
    (((h % 628) / 100 + index / Math.max(groupSize, 1)) % 1) * Math.PI * 2;
  const radiusT =
    JITTER_MIN_RADIUS_RATIO +
    (((h >>> 8) % 50) / 100) * (1 - JITTER_MIN_RADIUS_RATIO);
  const sizeBoost = 0.85 + Math.sqrt(groupSize) * 0.08;
  const radius = JITTER_MAX_RADIUS_DEG * radiusT * sizeBoost;
  return {
    dLng: radius * Math.cos(angle),
    dLat: radius * Math.sin(angle) * 0.9,
  };
}

/** 同经纬度多点紧凑散开；选中项落在真实坐标中心 */
export function buildCoordGroupOffsets(
  projects: MarketProject[],
  selectedProjectId: string | null,
): Map<string, { lng: number; lat: number }> {
  const groups = new Map<string, MarketProject[]>();
  for (const p of projects) {
    if (p.latitude == null || p.longitude == null) continue;
    const key = `${p.longitude.toFixed(COORD_KEY_PRECISION)},${p.latitude.toFixed(COORD_KEY_PRECISION)}`;
    const list = groups.get(key) ?? [];
    list.push(p);
    groups.set(key, list);
  }

  const out = new Map<string, { lng: number; lat: number }>();
  for (const group of Array.from(groups.values())) {
    if (group.length === 1) {
      const p = group[0];
      out.set(p.id, { lng: p.longitude!, lat: p.latitude! });
      continue;
    }
    const baseLng = group[0].longitude!;
    const baseLat = group[0].latitude!;
    const selectedInGroup =
      selectedProjectId != null &&
      group.some((p) => p.id === selectedProjectId);

    group.forEach((p, i) => {
      if (selectedInGroup && p.id === selectedProjectId) {
        out.set(p.id, { lng: baseLng, lat: baseLat });
        return;
      }
      const { dLng, dLat } = offsetForClusterMember(p.id, i, group.length);
      out.set(p.id, { lng: baseLng + dLng, lat: baseLat + dLat });
    });
  }
  return out;
}

function resolvePointColors(
  p: MarketProject,
  selectedId: string | null,
  selected: MarketProject | undefined,
): PointColors {
  if (!selectedId || !selected) {
    return { color: categoryColor(p.category, p.isOverseas), opacity: 1 };
  }
  if (selectedId === p.id) {
    return { color: '#ff4d4f', opacity: 1 };
  }
  if (
    !p.isOverseas &&
    !selected.isOverseas &&
    p.category === selected.category &&
    p.province === selected.province &&
    p.id !== selected.id
  ) {
    return { color: categoryColor(p.category, false), opacity: 0.35 };
  }
  return { color: categoryColor(p.category, p.isOverseas), opacity: 1 };
}

export function buildMarketScatterData(
  projects: MarketProject[],
  selectedProjectId: string | null,
): ScatterDatum[] {
  const selected = selectedProjectId
    ? projects.find((x) => x.id === selectedProjectId)
    : undefined;

  const mappable = projects.filter(
    (p) => p.latitude != null && p.longitude != null,
  );
  const jittered = buildCoordGroupOffsets(mappable, selectedProjectId);

  return mappable.map((p) => {
      const isSelected = selectedProjectId === p.id;
      const baseSize =
        p.isOverseas ? 12 : p.category === 'preliminary' ? 12 : 11;
      const { color, opacity } = resolvePointColors(
        p,
        selectedProjectId,
        selected,
      );
      const pos = jittered.get(p.id) ?? {
        lng: p.longitude!,
        lat: p.latitude!,
      };

      return {
        value: [pos.lng, pos.lat, SCATTER_ALTITUDE],
        name: p.name,
        parkId: p.id,
        city: p.city,
        parkLevel:
          p.category === 'preliminary' ? 'national' : 'provincial',
        projectCategory: p.category,
        isOverseas: p.isOverseas,
        overseasCountry: p.country,
        symbol: p.isOverseas ? 'diamond' : 'circle',
        symbolSize: isSelected ? baseSize + 3 : baseSize,
        itemStyle: {
          color,
          opacity,
          borderColor: isSelected ? '#fff2f0' : 'rgba(0, 184, 217, 0.65)',
          borderWidth: isSelected ? 2 : 1.5,
        },
      };
    });
}

export function buildMarketScatterSeries(
  data: ScatterDatum[],
): echarts.SeriesOption {
  return {
    type: 'scatter3D',
    name: 'marketProjects',
    coordinateSystem: 'geo3D',
    data,
    tooltip: { show: true },
    label: {
      show: true,
      formatter: '',
      position: 'top',
      distance: 4,
      color: '#ffccc7',
      fontWeight: 600,
    },
    emphasis: {
      disabled: true,
      label: { show: false, formatter: '' },
      itemStyle: { borderWidth: 2, borderColor: '#ffffff' },
    },
    blur: { label: { show: false, formatter: '' } },
    select: { label: { show: false, formatter: '' } },
  } as unknown as echarts.SeriesOption;
}
